class WECatalog extends HTMLElement {
  async connectedCallback() {
    if (this.started) return;
    this.started = true;
    this.config = JSON.parse(this.querySelector('[data-catalog-config]').textContent);
    this.logic = await import(this.config.logic_url);
    this.grid = this.querySelector('[data-grid]');
    this.controls = this.querySelector('[data-controls]');
    this.status = this.querySelector('[data-status]');
    this.dialog = this.querySelector('[data-filter-dialog]');
    this.sidebar = this.querySelector('[data-sidebar]');
    this.slot = this.querySelector('[data-sidebar-slot]');
    this.queryInput = this.querySelector('input[name=q]');
    this.generation = 0;
    this.searchCache = new Map();
    this.state = this.readState();
    this.batchSize = Number(this.config.batch_size) || 48;
    this.restorePending = this.readPosition();
    this.limit = this.restorePending?.limit || this.batchSize;
    this.querySelector('[data-catalog-search]').addEventListener('submit', event => {
      event.preventDefault();
      this.state.query = this.queryInput.value.trim();
      this.changed();
    });
    this.controls.addEventListener('change', event => {
      const input = event.target;
      if (input.dataset.group) {
        const group = input.dataset.group;
        this.state[group] = [...this.controls.querySelectorAll(`input[data-group="${group}"]:checked`)].map(option => option.value);
        if (group === 'types') for (const type of Object.keys(this.state.sizes)) if (!this.state.types.includes(type)) delete this.state.sizes[type];
      } else if (input.hasAttribute('data-sort')) this.state.sort = input.value;
      else if (input.dataset.sizeFor) this.state.sizes[input.dataset.sizeFor] = input.value;
      this.changed();
    });
    this.querySelector('[data-clear]').addEventListener('click', () => {
      this.state.types = []; this.state.colors = []; this.state.collections = []; this.state.sizes = {};
      this.changed();
    });
    this.querySelector('[data-load-more]').addEventListener('click', () => {
      const firstNew = this.limit;
      this.limit += Number(this.config.batch_size);
      this.renderProducts(true);
      this.grid.children[firstNew]?.querySelector('a')?.focus({preventScroll:true});
      this.savePosition();
    });
    this.querySelector('[data-open-filters]').addEventListener('click', () => {
      this.dialog.append(this.sidebar);
      this.dialog.showModal();
    });
    for (const button of this.querySelectorAll('[data-close-filters],[data-show-results]')) button.addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('close', () => this.slot.append(this.sidebar));
    this.media = matchMedia('(min-width:900px)');
    this.onResize = () => { if (this.media.matches && this.dialog.open) this.dialog.close(); };
    this.media.addEventListener('change', this.onResize);
    this.onSavePosition = () => this.savePosition();
    window.addEventListener('pagehide', this.onSavePosition);
    this.grid.addEventListener('click', this.onSavePosition);
    this.onPopState = () => {
      cancelAnimationFrame(this.restoreFrame);
      this.state = this.readState();
      this.restorePending = this.readPosition();
      this.limit = this.restorePending?.limit || this.batchSize;
      this.refresh();
    };
    window.addEventListener('popstate', this.onPopState);
    this.querySelector('[data-retry]').addEventListener('click', () => this.allProducts ? this.refresh() : this.initialize());
    this.querySelector('[data-browse-all]').addEventListener('click', () => {
      this.state.query = ''; this.state.types = []; this.state.colors = []; this.state.collections = []; this.state.sizes = {};
      this.changed();
    });
    await this.initialize();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
    window.removeEventListener('pagehide', this.onSavePosition);
    this.grid?.removeEventListener('click', this.onSavePosition);
    cancelAnimationFrame(this.restoreFrame);
    this.media?.removeEventListener('change', this.onResize);
    this.abort?.abort();
  }

  readPosition() {
    const saved = history.state?.weCatalog;
    if (saved?.url !== location.href || !Number.isInteger(saved.limit) || saved.limit < this.batchSize || saved.limit > 25000) return null;
    return { ...saved, scrollY: Number.isFinite(saved.scrollY) ? Math.max(0, saved.scrollY) : 0 };
  }

  savePosition() {
    if (!this.results || this.restorePending) return;
    const active = document.activeElement?.closest('.we-catalog-card__link');
    history.replaceState({ ...history.state, weCatalog: {
      url: location.href, limit: this.limit, scrollY: scrollY, focus: active?.getAttribute('href') || ''
    } }, '', location.href);
  }

  restorePosition() {
    const saved = this.restorePending;
    if (!saved) return;
    this.restorePending = null;
    this.restoreFrame = requestAnimationFrame(() => {
      this.restoreFrame = requestAnimationFrame(() => {
        if (saved.url !== location.href) return;
        window.scrollTo({ top: saved.scrollY, behavior: 'instant' });
        const link = [...this.grid.querySelectorAll('.we-catalog-card__link')].find(node => node.getAttribute('href') === saved.focus);
        link?.focus({ preventScroll: true });
      });
    });
  }

  readState() {
    const url = new URL(location.href);
    const known = (key, list) => url.searchParams.getAll(key).filter(id => list.some(option => option.id === id));
    const state = {types:known('we_type',this.config.types), colors:known('we_color',this.config.colors), collections:known('we_collection',this.config.collections), sizes:{}, query:url.searchParams.get('q') || '', sort:url.searchParams.get('sort_by') || 'best-selling'};
    if (!url.searchParams.has('we_browse')) {
      const handle = url.pathname.split('/collections/')[1]?.split('/')[0];
      if (this.config.types.some(type => type.id === handle)) state.types = [handle];
      else if (this.config.collections.some(collection => collection.id === handle)) state.collections = [handle];
    }
    if (!['best-selling','created-descending','price-ascending','price-descending'].includes(state.sort)) state.sort = 'best-selling';
    for (const type of state.types) {
      const size = url.searchParams.get(`we_size_${type}`);
      if (size) state.sizes[type] = this.logic.normalizeSize(size);
    }
    return state;
  }

  writeState() {
    const previous = new URL(location.href);
    const url = new URL(this.config.browse_url, location.origin);
    if (previous.searchParams.has('preview_theme_id')) url.searchParams.set('preview_theme_id', previous.searchParams.get('preview_theme_id'));
    url.searchParams.set('we_browse', '1');
    if (this.state.query) url.searchParams.set('q', this.state.query);
    for (const [group, key] of [['types','we_type'],['colors','we_color'],['collections','we_collection']]) for (const value of this.state[group]) url.searchParams.append(key,value);
    for (const [type,size] of Object.entries(this.state.sizes)) if (size && this.state.types.includes(type)) url.searchParams.set(`we_size_${type}`,size);
    if (this.state.sort !== 'best-selling') url.searchParams.set('sort_by',this.state.sort);
    const position = { weCatalog: { url: url.href, limit: this.limit, scrollY: scrollY, focus: '' } };
    if (url.href !== previous.href) history.pushState(position,'',url);
    else history.replaceState({ ...history.state, ...position },'',url);
  }

  changed() {
    const saleNotice = this.querySelector('[data-sale-collection-notice]');
    if (saleNotice) saleNotice.hidden = this.state.collections.length !== 1 || this.state.collections[0] !== saleNotice.dataset.saleCollectionNotice;
    this.savePosition();
    cancelAnimationFrame(this.restoreFrame);
    this.restorePending = null;
    this.limit = this.batchSize;
    this.writeState();
    if (this.allProducts) this.refresh(); else this.initialize();
  }

  requestURL(path, query = '') {
    const url = new URL(path,location.origin);
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id',preview);
    if (query) {
      url.searchParams.set('q',query);
      url.searchParams.set('type','product');
      url.searchParams.set('options[prefix]','last');
    } else url.searchParams.set('sort_by','best-selling');
    url.searchParams.set('view','we-catalog-data');
    url.searchParams.set('_we_fetch',String(Date.now()));
    return url;
  }

  async fetchPage(url, signal) {
    let response;
    for (let attempt = 0; attempt < 3; attempt++) {
      response = await fetch(url,{credentials:'same-origin',signal});
      if (![429,503].includes(response.status) || attempt === 2) break;
      const retryAfter = Number(response.headers.get('Retry-After')) * 1000;
      const delay = Math.min(5000,Math.max(500,retryAfter || 500 * 2 ** attempt));
      await new Promise((resolve,reject) => {
        signal?.throwIfAborted();
        const aborted = () => { clearTimeout(timer); reject(signal.reason); };
        const timer = setTimeout(() => { signal?.removeEventListener('abort',aborted); resolve(); },delay);
        signal?.addEventListener('abort',aborted,{once:true});
      });
    }
    if (!response.ok) throw new Error('Catalog request failed');
    const data = await response.json();
    if (!Array.isArray(data.items) || !Number.isInteger(data.total)) throw new Error('Invalid catalog response');
    return data;
  }

  async fetchAll(url, signal, attempt = 0) {
    try { return await this.fetchCatalogPages(url,signal); }
    catch (error) {
      if (attempt === 0 && /^(Catalog changed during loading|Incomplete catalog)/.test(error.message)) {
        const retryURL = new URL(url);
        retryURL.searchParams.set('_we_fetch',`${Date.now()}-retry`);
        return this.fetchAll(retryURL,signal,1);
      }
      throw error;
    }
  }

  async fetchCatalogPages(url, signal) {
    const first = await this.fetchPage(url,signal);
    if (first.total > 25000 || first.pages > 100) throw new Error('Catalog exceeds pagination limit');
    const pages = [first.items];
    let nextPage = 2;
    const worker = async () => {
      while (nextPage <= first.pages) {
        const page = nextPage++;
        const pageURL = new URL(url);
        pageURL.searchParams.set('page',String(page));
        const data = await this.fetchPage(pageURL,signal);
        if (data.total !== first.total || data.page !== page) throw new Error(`Catalog changed during loading (${url.pathname}): page ${data.page}/${page}, count ${data.total}/${first.total}`);
        pages[page - 1] = data.items;
      }
    };
    await Promise.all(Array.from({length:Math.min(3,Math.max(0,first.pages - 1))},worker));
    const items = pages.flat();
    if (items.length !== first.total || new Set(items.map(item => item.id)).size !== first.total) throw new Error(`Incomplete catalog: ${items.length} rows, ${new Set(items.map(item => item.id)).size} unique, ${first.total} expected`);
    return items.map(item => {
      const typeHandles = this.config.types.filter(type => type.product_type && type.product_type.toLowerCase() === (item.product_type || '').trim().toLowerCase()).map(type => type.id);
      const collections = [...new Set([...item.collections,...typeHandles])];
      return {...item, collections, searchText:this.logic.indexSearchText({...item,collections}), sizes:[...new Set(item.sizes.map(this.logic.normalizeSize))]};
    });
  }

  async initialize() {
    this.controls.disabled = true;
    this.status.textContent = this.config.text.loading;
    this.querySelector('[data-retry]').hidden = true;
    this.querySelector('[data-browse-all]').hidden = true;
    this.abort?.abort();
    this.abort = new AbortController();
    try {
      this.allProducts = await this.fetchAll(this.requestURL(this.config.all_url),this.abort.signal);
      this.rank = new Map(this.allProducts.map((product,index) => [product.id,index]));
      this.setupSizes();
      await this.refresh();
    } catch (error) { if (error.name !== 'AbortError') this.failed(error); }
  }

  setupSizes() {
    for (const type of this.config.types) {
      const products = this.allProducts.filter(product => product.collections.includes(type.id));
      const sizes = [...new Set(products.flatMap(product => product.sizes))].sort((a,b) => a.localeCompare(b,undefined,{numeric:true}));
      const uniform = products.length > 0 && products.every(product => product.sizes.length === sizes.length && sizes.every(size => product.sizes.includes(size)));
      const container = this.querySelector(`[data-type-block="${type.id}"]`);
      const select = container.querySelector('select');
      select.replaceChildren(new Option(this.config.text.all_sizes,''),...sizes.map(size => new Option(this.sizeLabel(size),size)));
      const note = container.querySelector('[data-size-note]');
      container.dataset.hasSizes = String(sizes.length > 0);
      container.dataset.uniformSizes = String(uniform);
      if (uniform) note.textContent = this.config.text.uniform_sizes.replace('__SIZES__',sizes.map(size => this.sizeLabel(size)).join(' / '));
      note.hidden = !uniform;
      select.hidden = uniform;
      container.querySelector('[data-size-label]').hidden = uniform;
      if (this.state.sizes[type.id] && !sizes.includes(this.state.sizes[type.id])) delete this.state.sizes[type.id];
    }
  }

  async fetchGuides(query, signal) {
    const url = new URL(this.config.search_url,location.origin);
    url.searchParams.set('view','we-guide-data');
    url.searchParams.set('type','article');
    url.searchParams.set('q',query);
    url.searchParams.set('options[prefix]','last');
    url.searchParams.set('_we_fetch',String(Date.now()));
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id',preview);
    try { return (await this.fetchPage(url,signal)).items; } catch (error) { if (error.name === 'AbortError') throw error; return []; }
  }

  async refresh() {
    if (!this.allProducts) return;
    const generation = ++this.generation;
    this.abort?.abort();
    this.abort = new AbortController();
    this.controls.disabled = true;
    this.status.textContent = this.config.text.loading;
    this.querySelector('[data-retry]').hidden = true;
    this.querySelector('[data-browse-all]').hidden = true;
    try {
      const query = this.state.query;
      let products = this.allProducts;
      let guides = [];
      if (query) {
        let cached = this.searchCache.get(query);
        if (!cached) {
          cached = {products:this.logic.searchProducts(this.allProducts,query),guides:await this.fetchGuides(query,this.abort.signal)};
          this.searchCache.set(query,cached);
          if (this.searchCache.size > 5) this.searchCache.delete(this.searchCache.keys().next().value);
        }
        products = cached.products; guides = cached.guides;
      }
      if (generation !== this.generation) return;
      this.baseProducts = products;
      this.results = this.logic.selectProducts(products,this.state,this.rank);
      this.syncControls();
      this.renderChips();
      this.renderGuides(guides);
      this.renderProducts(false);
      this.controls.disabled = false;
      this.querySelector('[data-fallback]').hidden = true;
      this.restorePosition();
      this.status.textContent = this.config.text.showing.replace('__SHOWN__',this.format(Math.min(this.limit,this.results.length))).replace('__TOTAL__',this.format(this.results.length));
    } catch (error) { if (error.name !== 'AbortError' && generation === this.generation) this.failed(error); }
  }

  failed(error) {
    console.error('Catalog filters:',error?.message || 'Loading failed');
    this.status.textContent = this.config.text.error;
    this.controls.disabled = !this.allProducts;
    this.results = [];
    this.grid.replaceChildren();
    this.renderGuides([]);
    this.renderChips();
    this.querySelector('[data-results-heading]').textContent = this.config.text.results_unavailable;
    this.querySelector('[data-show-results]').textContent = this.config.text.results_unavailable;
    this.querySelector('[data-empty]').hidden = true;
    this.querySelector('[data-load-more]').hidden = true;
    this.querySelector('[data-showing]').textContent = '';
    this.querySelector('[data-fallback]').hidden = true;
    this.querySelector('[data-retry]').hidden = false;
    this.querySelector('[data-browse-all]').hidden = false;
  }

  format(number) { return new Intl.NumberFormat(document.documentElement.lang || 'en').format(number); }
  sizeLabel(size) { return /^\d+(\.\d+)? x \d+(\.\d+)?$/.test(size) ? size.split(' x ').map(value => `${value}″`).join(' × ') : size; }

  syncControls() {
    const saleNotice = this.querySelector('[data-sale-collection-notice]');
    if (saleNotice) saleNotice.hidden = this.state.collections.length !== 1 || this.state.collections[0] !== saleNotice.dataset.saleCollectionNotice;
    this.queryInput.value = this.state.query;
    this.querySelector('[data-sort]').value = this.state.sort;
    for (const input of this.controls.querySelectorAll('input[data-group]')) {
      const group = input.dataset.group;
      input.checked = this.state[group].includes(input.value);
      const count = this.baseProducts.filter(product => this.logic.matchesFilters(product,this.state,group) && product[group === 'types' ? 'collections' : group].includes(input.value)).length;
      input.disabled = count === 0 && !input.checked;
      input.closest('label').querySelector('[data-choice-count]').textContent = `(${this.format(count)})`;
    }
    for (const type of this.config.types) {
      const container = this.querySelector(`[data-type-block="${type.id}"]`);
      container.querySelector('[data-size-container]').hidden = !this.state.types.includes(type.id) || container.dataset.hasSizes !== 'true';
      container.querySelector('select').value = this.state.sizes[type.id] || '';
    }
    this.querySelector('[data-results-heading]').textContent = `${this.config.text.results}: ${this.format(this.results.length)}`;
    this.querySelector('[data-show-results]').textContent = this.config.text.show_results.replace('__COUNT__',this.format(this.results.length));
  }

  renderChips() {
    const fragment = document.createDocumentFragment();
    const chip = (label,remove) => {
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'we-catalog__chip';
      button.textContent = `${label} ×`;
      button.setAttribute('aria-label',this.config.text.remove.replace('__VALUE__',label));
      button.addEventListener('click',() => { remove(); this.changed(); });
      fragment.append(button);
    };
    if (this.state.query) chip(this.config.text.query_chip.replace('__QUERY__',this.state.query),() => {this.state.query = '';});
    for (const group of ['types','colors','collections']) for (const id of this.state[group]) {
      const label = this.config[group].find(option => option.id === id)?.label || id;
      chip(label,() => { this.state[group] = this.state[group].filter(value => value !== id); if (group === 'types') delete this.state.sizes[id]; });
    }
    for (const [type,size] of Object.entries(this.state.sizes)) if (size) chip(`${this.config.types.find(option => option.id === type)?.label}: ${this.sizeLabel(size)}`,() => {delete this.state.sizes[type];});
    this.querySelector('[data-chips]').replaceChildren(fragment);
  }

  renderGuides(guides) {
    const container = this.querySelector('[data-guides]');
    container.replaceChildren(); container.hidden = guides.length === 0;
    if (!guides.length) return;
    const heading = document.createElement('h2'); heading.textContent = this.config.text.guide_results;
    const list = document.createElement('ul');
    for (const guide of guides) { const li = document.createElement('li'); const link = document.createElement('a'); link.href = guide.url; link.textContent = guide.title; li.append(link); list.append(li); }
    container.append(heading,list);
  }

  renderProducts(append) {
    const fragment = document.createDocumentFragment();
    const start = append ? this.grid.children.length : 0;
    for (const product of this.results.slice(start,this.limit)) {
      const li = document.createElement('li'); li.className = 'we-catalog-card'; li.dataset.productId = product.id;
      const link = document.createElement('a'); link.className = 'we-catalog-card__link'; link.href = product.url;
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) { const url = new URL(product.url,location.origin); url.searchParams.set('preview_theme_id',preview); link.href = url.href; }
      link.setAttribute('aria-label',product.title);
      link.dataset.breadcrumbProductOrigin = 'search'; link.dataset.breadcrumbOriginTitle = this.config.text.results;
      const media = document.createElement('div'); media.className = 'we-catalog-card__media';
      if (product.image) {
        const image = document.createElement('img'); image.src = product.image;
        image.srcset = [200,320,480,640].map(width => { const url = new URL(product.image,location.origin); url.searchParams.set('width',width); return `${url.href} ${width}w`; }).join(', ');
        image.sizes = '(max-width:599px) 46vw, (max-width:899px) 30vw, (max-width:1199px) 24vw, 20vw';
        image.alt = product.title; image.loading = 'lazy'; image.decoding = 'async'; image.width = 640; image.height = 640; media.append(image);
      }
      if (product.badge) { const badge = document.createElement('span'); badge.className = 'we-catalog-card__badge color-custom-badge-sale'; badge.textContent = product.badge; media.append(badge); }
      const title = document.createElement('h2'); title.className = 'we-catalog-card__title'; title.textContent = product.title;
      title.title = product.title;
      const price = document.createElement('div'); price.className = 'we-catalog-card__price'; price.innerHTML = product.price_html;
      if (product.price_varies) price.prepend(document.createTextNode(`${this.config.text.from} `));
      link.append(media,title); li.append(link,price); fragment.append(li);
    }
    if (!append) this.grid.replaceChildren(fragment); else this.grid.append(fragment);
    this.querySelector('[data-empty]').hidden = this.results.length !== 0;
    this.querySelector('[data-load-more]').hidden = this.limit >= this.results.length;
    this.querySelector('[data-showing]').textContent = this.config.text.showing.replace('__SHOWN__',this.format(Math.min(this.limit,this.results.length))).replace('__TOTAL__',this.format(this.results.length));
    if (append) this.status.textContent = this.querySelector('[data-showing]').textContent;
  }
}

if (!customElements.get('we-catalog')) customElements.define('we-catalog',WECatalog);
